// Приём выгрузки из мессенджера (сценарий 112).
//
// ## Почему текст вставляется прямо сюда
//
// Готового чёрного списка заказчик не передавал: он живёт перепиской в группах «Нарушители» и
// «Кандидаты». Выгрузка такой группы приходит текстом, а хранилище файлов принимает пока
// только изображения и pdf — значит единственный работающий путь это вставка. Так и написано
// в контракте у поля `file_id`.
//
// ## На экране одна кнопка, а на сервере два действия
//
// Сначала заводится выгрузка, потом запускается её разбор. Разделять это на два нажатия
// незачем: человеку нужен результат, а не наши шаги. Но если второе действие упало, делать
// вид, что ничего не произошло, нельзя — выгрузка УЖЕ создана, и второе нажатие «Принять и
// разобрать» завело бы вторую такую же.
//
// Хуже того, повторить разбор из карточки выгрузки в этом случае нечем: вставленный текст
// нигде не хранится, он уходит прямо в разбор. Поэтому повтор живёт здесь, пока текст ещё
// в форме, и кнопка после сбоя меняет смысл — «Повторить разбор», а не «Принять и разобрать».

import type { FormEvent } from 'react';
import { useState } from 'react';

import { RiCheckboxCircleLine } from '@remixicon/react';

import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';

import type { MessengerGroup } from '../api/blacklist';
import { GateNote, NoPermissionNote, useCan } from '../components/gates';
import { MESSENGER_GROUP_TITLES } from '../dictionaries';
import { useCreateImportMutation, useParseImportMutation } from '../queries';
import { FILTER_IMPORT, hiringHref, setFilter } from '../route';

import { parseSearch, popPanel } from '@/shared/routing/search';
import { useLocation, useNavigate } from 'react-router';

/** Приём и разбор выгрузки: право рекрутёра и администратора. */
const EDIT_PERMISSION = 'hiring.blacklist.edit';

/** Сколько строк выгрузки видно без прокрутки. Переписка — это не одна фраза. */
const ROWS = 12;

type FieldName = 'group' | 'text';

export function NewImportPanel({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const allowed = useCan(EDIT_PERMISSION);

  const создание = useCreateImportMutation();
  const разбор = useParseImportMutation();

  const [group, setGroup] = useState(NO_VALUE);
  const [text, setText] = useState('');
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [gateNote, setGateNote] = useState<string | null>(null);
  // Опознаватель уже созданной выгрузки. Пока он здесь, второе нажатие повторяет РАЗБОР, а не
  // заводит вторую выгрузку.
  const [созданная, setСозданная] = useState<string | null>(null);

  const идёт = создание.isPending || разбор.isPending;

  function открытьРазобранное(importId: string) {
    // Человек только что принял выгрузку и хочет увидеть, что из неё вышло. Отбор по этой
    // выгрузке ставится сразу: иначе он попадёт в общий список предложений и будет искать
    // свои строки среди чужих.
    const search = setFilter(popPanel(parseSearch(location.search)), FILTER_IMPORT, importId);
    navigate(hiringHref('drafts', search));
  }

  async function отправить() {
    let importId = созданная;
    if (importId === null) {
      const выгрузка = await создание.mutateAsync({
        groupKind: group as MessengerGroup,
        fileId: null,
      });
      importId = выгрузка.id;
      setСозданная(выгрузка.id);
    }
    await разбор.mutateAsync({ importId, text: text.trim() });
    открытьРазобранное(importId);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed) {
      setGateNote(
        'Принимать выгрузки может рекрутёр или администратор. Попросите открыть это право — вставленный текст никуда не денется.',
      );
      return;
    }
    if (идёт) {
      setGateNote('Выгрузка уже обрабатывается, ждём ответ сервера. Второй раз не отправляем.');
      return;
    }

    const next: Partial<Record<FieldName, string>> = {};
    if (group === NO_VALUE) {
      next.group = 'Выберите группу: от неё зависит, как модель читает переписку.';
    }
    if (text.trim() === '') {
      next.text = 'Вставьте текст выгрузки — разбирать пока нечего.';
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }

    setErrors({});
    setGateNote(null);
    // Ошибку показывают сами мутации: их состояние и есть источник правды о том, что упало.
    void отправить().catch(() => undefined);
  }

  return (
    <form onSubmit={handleSubmit} noValidate className='flex flex-col gap-3'>
      {!allowed && (
        <NoPermissionNote
          что='принимать выгрузки из мессенджера'
          кто='Это делают рекрутёр и администратор системы.'
        />
      )}

      <p className='text-paragraph-sm text-text-sub-600'>
        Разбор даёт предложения, а не записи. В чёрный список отсюда не попадает никто: по каждой
        строке решение принимает человек.
      </p>

      <SelectField
        id='new-import-group'
        label='Группа мессенджера'
        required
        value={group}
        onChange={setGroup}
        options={(Object.keys(MESSENGER_GROUP_TITLES) as MessengerGroup[]).map((code) => ({
          value: code,
          label: MESSENGER_GROUP_TITLES[code],
        }))}
        placeholder='Выберите группу'
        error={errors.group}
        hint='«Нарушители» — переписка о тех, кого больше не берут. «Кандидаты» — обсуждение людей на найме.'
      />

      <TextArea
        id='new-import-text'
        label='Текст выгрузки'
        required
        rows={ROWS}
        value={text}
        onChange={setText}
        placeholder='Вставьте сюда переписку целиком'
        error={errors.text}
        hint='Файл выгрузки система пока не принимает — только текст. Скопируйте переписку из мессенджера и вставьте её сюда как есть, вместе с датами и именами.'
      />

      {/* Выгрузка создана, а разбор упал. Молчать об этом нельзя: со стороны это выглядит как
          «ничего не произошло», и следующее нажатие завело бы вторую такую же выгрузку. */}
      {созданная !== null && разбор.isError && (
        <Alert.Root variant='light' status='warning' size='small' role='status'>
          <Alert.Icon as={RiCheckboxCircleLine} />
          <span>
            Выгрузка уже принята, разобрать её не удалось. Второй раз принимать не надо — нажмите
            «Повторить разбор», текст для этого остался в поле выше.
          </span>
        </Alert.Root>
      )}

      {создание.isError && <ErrorAlert error={создание.error} />}
      {разбор.isError && <ErrorAlert error={разбор.error} />}

      {gateNote !== null && <GateNote>{gateNote}</GateNote>}

      <div className='flex items-center gap-2 pt-1'>
        <Button.Root type='submit' variant='primary' mode='filled' size='small'>
          {идёт ? 'Разбираем…' : созданная === null ? 'Принять и разобрать' : 'Повторить разбор'}
        </Button.Root>
        <Button.Root type='button' variant='neutral' mode='stroke' size='small' onClick={onClose}>
          {созданная === null ? 'Отмена' : 'Закрыть'}
        </Button.Root>
      </div>
    </form>
  );
}
